import { h as __h } from "preact";
import { Component, h } from "preact";
/** @import { ComponentChildren } from "preact"; */

import { auditLogResponse, enterpriseOrgResponse } from "~/backend/validate.js";
/** @import { auditActorResponse, sessionEnterpriseResponse } from "~/backend/validate.js"; */
import { paginatedResponse, query } from "~/lib/api.js";
import html from "~/util/html.js";
import Avatar from "~/component/Avatar.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import Time, { timeOnDate } from "~/component/Time.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import DetailList, { DetailTable, isRecord } from "~/admin/component/DetailList.js";
import Pagination from "~/admin/component/Pagination.js";
import Table from "~/admin/component/Table.js";
import Roles from "~/admin/lib/roles.js";
import adminStyle from "~/admin/lib/style.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./AuditLogs.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./AuditLogs.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {sessionEnterpriseResponse} enterprise
 */
export default class AuditLogs extends Component {
  paginate = new Pagination.State();
  logs = query(() => ({
    key: [`/v2/enterprises/${this.props.enterprise.id}/audit-logs`, this.paginate.query()],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(auditLogResponse)
  });
  orgs = query(() => ({
    key: [`/v2/enterprises/${this.props.enterprise.id}/orgs`, {
      limit: 9999
    }]
  }), {
    schema: paginatedResponse(enterpriseOrgResponse)
  });
  render() {
    const logs = this.logs;
    const orgNames = new Map(this.orgs.data?.data.map(org => [org.id, org.name]));
    const rows = logs.data?.data.map(log => ({
      key: log.id,
      label: `audit log ${log.id}`,
      value: log,
      columns: {
        time: __h("span", {
          title: log.created_at
        }, __h(Time, {
          fmt: timeOnDate,
          datetime: new Date(log.created_at)
        })),
        actor: h(ActorChip, {
          actor: log.actor
        }),
        ip: log.ip_address,
        action: __h("code", {}, log.action),
        org: log.org_id ? h(OrgName, {
          id: log.org_id,
          name: orgNames.get(log.org_id)
        }) : ""
      }
    }));
    return [__h(Crumb, {}, "Audit Log"), __h("div", {
      "class": adminStyle.main
    }, __h("div", {
      "class": adminStyle.body
    }, __h("p", {
      "class": css.note
    }, "Audit logs are retained for 90 days."), __h("div", {
      "class": util.card + " " + adminStyle.tableCard
    }, h(Data, {
      loading: logs.loading && !logs.data,
      error: logs.error,
      children: h(/** @type {typeof Table<auditLogResponse>} */Table, {
        label: "audit log",
        headers: [{
          id: "time",
          label: "Time"
        }, {
          id: "actor",
          label: "Actor",
          width: "16%"
        }, {
          id: "ip",
          label: "IP address",
          width: "15%"
        }, {
          id: "action",
          label: "Action",
          width: "27%"
        }, {
          id: "org",
          label: "Organization"
        }],
        empty: "No audit events in the last 90 days.",
        rows: rows ?? [],
        expand: log => log.details === undefined ? null : __h("div", {
          "class": css.details
        }, __h(AuditDetails, {
          log: log
        })),
        paginate: this.paginate.props(logs)
      })
    }))))];
  }
}

/**
 * @typedef {object} OrgNameProps
 * @property {string} id
 * @property {string} [name]
 */

/** @param {OrgNameProps} props */
function OrgName({
  id,
  name
}) {
  if (!name) return id;
  return h(IconName, {
    icon: __h(Avatar, {
      name: name,
      size: 16
    }),
    name
  });
}

/**
 * @typedef {object} IconNameProps
 * @property {ComponentChildren} icon
 * @property {string} name
 * @property {string} [title]
 */

/** @param {IconNameProps} props */
function IconName({
  icon,
  name,
  title
}) {
  return __h("span", {
    "class": css.iconName,
    title: title
  }, icon, __h("span", {
    "class": css.name
  }, name));
}

/**
 * @typedef {object} ActorChipProps
 * @property {auditActorResponse} actor
 */

/** @param {ActorChipProps} props */
function ActorChip({
  actor
}) {
  if (actor.type === "api_key") {
    return __h("a", {
      href: "/settings",
      "class": adminStyle.chip,
      title: "API key"
    }, __h(Icon, {
      name: "key",
      size: 12
    }), __h("span", {}, actor.name));
  }
  return h(UserChip, {
    name: actor.name,
    email: actor.email
  });
}

/**
 * @typedef {object} UserChipProps
 * @property {string} name
 * @property {string} [email]
 */

/** @param {UserChipProps} props */
function UserChip({
  name,
  email
}) {
  return __h("a", {
    href: `/members?q=${encodeURIComponent(email ?? name)}`,
    "class": adminStyle.chip,
    title: email
  }, __h(Avatar, {
    name: name,
    size: 12
  }), __h("span", {}, name));
}

/**
 * Detail fields that are only useful to Phaze, by action.
 *
 * @type {Record<string, string[]>}
 */
const hiddenDetails = {
  "connection.connect": ["attempt_id"],
  "enterprise_api_key.create": ["api_key_id"]
};

/**
 * @typedef {object} AuditDetailsProps
 * @property {auditLogResponse} log
 */

/** @param {AuditDetailsProps} props */
function AuditDetails({
  log
}) {
  const value = {
    ...withMachine(isRecord(log.details) ? log.details : {
      details: log.details
    })
  };
  for (const key of hiddenDetails[log.action] ?? []) delete value[key];
  const keys = Object.keys(value);
  const scope = log.action.startsWith("enterprise_member.") ? "enterprise" : "org";
  /** @type {import("~/admin/component/DetailList.js").DetailRenderer} */
  const render = (key, value) => renderDetail(key, value, scope);
  if (keys.length === 1 && keys[0] === "members") {
    const members = render("members", value.members);
    if (members !== undefined) return members;
  }
  return __h(DetailList, {
    value: value,
    render: render
  });
}

/**
 * Puts `machine_id` and `machine_name` together in one `machine` entry, so that the name shows
 * in place of the ID.
 *
 * @param {Record<string, unknown>} details
 * @returns {Record<string, unknown>}
 */
function withMachine(details) {
  const {
    machine_id: id,
    machine_name: name,
    ...rest
  } = details;
  if (typeof id !== "string" || typeof name !== "string" || !name) return details;
  return {
    machine: {
      id,
      name
    },
    ...rest
  };
}
const controlTypes = [{
  value: "ui",
  label: "Human",
  icon: "person"
}, {
  value: "mcp",
  label: "Agent",
  icon: "robot"
}];

/**
 * @param {string} key
 * @param {unknown} value
 * @param {import("~/admin/lib/roles.js").RoleScope} scope
 * @returns {ComponentChildren | undefined}
 */
function renderDetail(key, value, scope) {
  const controlType = key === "control_type" && controlTypes.find(type => type.value === value);
  if (controlType) {
    return h(IconName, {
      icon: __h(Icon, {
        name: controlType.icon,
        size: 16
      }),
      name: controlType.label
    });
  }
  if (key === "machine" && isRecord(value) && typeof value.name === "string") {
    return h(IconName, {
      icon: __h(Icon, {
        name: "machine",
        size: 16
      }),
      name: value.name,
      title: typeof value.id === "string" ? value.id : undefined
    });
  }
  if (key === "members" && Array.isArray(value) && value.every(isRoleChange)) {
    return __h(DetailTable, {
      rows: value.map(member => ({
        key: member.user_id,
        label: __h(UserChip, {
          name: member.name,
          email: member.email
        }),
        value: [member.previous_role && [Roles.label(scope, member.previous_role), __h("span", {
          "class": css.arrow
        }, " \u2192 ")], Roles.label(scope, member.role)]
      }))
    });
  }
  return undefined;
}

/**
 * @typedef {object} RoleChange
 * @property {string} user_id
 * @property {string} name
 * @property {string} [email]
 * @property {string} role
 * @property {string | null} [previous_role]
 */

/**
 * @param {unknown} value
 * @returns {value is RoleChange}
 */
function isRoleChange(value) {
  return isRecord(value) && typeof value.user_id === "string" && typeof value.name === "string" && typeof value.role === "string";
}